// ./src/components/features/Events/EventPlansDrawer.tsx
//
// "What this event needs, and the upgrade that gives it" — the event dashboard's URL stage `/events/:eventId/plans`.
//
// ROADMAP #231 + #209 (owner, 2026-10-22: "let user use any gated feature on draft event and just do not let publish it
// until they upgrade … For eventsmagic user needs to subscribe, for partylabz upgrade this event right when publishing"):
//   • what publishing would refuse comes from the SERVER's own check (`eventPlanCheck`), never worked out here;
//   • partylabz (a free account — `eventUpgradeOffer`): the upgrades of THIS event, each saying whether it covers that
//     list, paid in place, then — opened from Publish — the event is published as soon as the upgrade is granted (owner,
//     2026-10-26: "Pay, then publish"). An upgraded event is offered only the plans ABOVE its own, at the difference
//     (owner, 2026-10-26: "allow VIP to Signature upgrade" — priced by ms_auth, the same rule it charges by);
//   • eventsmagic, or a subscriber: the list, and the way to the subscription.
//   • an upgraded event still sees every plan, as before it was upgraded (owner, 2026-10-28: "show about the same as
//     before the upgrade but disable the choice of upgrade plan and show this is the event's current plan"): its own
//     marked and what it gives, the ones below it included in it, the ones above for sale.
// Either way the other way out is named: take the premium things off the event.
//
// ROADMAP #120: a drawer, not a modal; a URL stage on the dashboard.
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Group, List, Loader, SimpleGrid, Stack, Text, ThemeIcon } from "@mantine/core";
import { Elements } from "@stripe/react-stripe-js";
import { useTranslation } from "react-i18next";
import { formatWithCurrencyLabel } from "@em/utils";

import { AppDrawer, AppIcon } from "@/components/generic";
import { useCrewAccess } from "@/contexts/CrewAccessContext/CrewAccessContext";
import { useEventUpgrade } from "@/hooks/useEventUpgrade";
import { formatOfferCountdown, useOfferCountdown } from "@/hooks/useOfferCountdown";
import type { EventUpgradeOption } from "@/utils/api";
import { planUseLabel, upgradeCoverage } from "@/utils/eventUpgrade";
import { featureDisplayName, isBooleanFeature, planFeatureMapOf } from "@/utils/planEntitlement";
import { refusalReason } from "@/utils";
import { PlansSource, trackUpgradeCheckout, trackUpgradeView } from "@/utils/analytics";

import { getPlatformStripe, PlatformPayStep } from "../Billing/PlatformPayStep";

export interface EventPlansDrawerProps {
  opened: boolean;
  onClose: () => void;
  eventId: string;
  /**
   * What the event is about to do, for the words: `publish`, or `go_live` (end test mode on a public event — the host then
   * switches with its own confirm, which says what happens to the test records).
   */
  doing: "publish" | "go_live";
  /** partylabz, a free account (`eventUpgradeOffer` = offer): this event can be upgraded here. Otherwise the subscription. */
  offerUpgrade: boolean;
  /**
   * Opened from Publish: the drawer offers Publish once nothing is missing, and publishes by itself when an upgrade bought
   * here is granted. The dashboard's own publish (its email step and toasts included).
   */
  onPublish?: () => void;
  /** Where a subscription is chosen (eventsmagic, or partylabz's frequent host). */
  onSubscribe: () => void;
  /** Where features are switched off. */
  onOpenFeatures: () => void;
  /** #60 — which prompt opened the plans (`plansSourceOf`), for the funnel's analytics. */
  source: PlansSource;
}

/** How long a payment may confirm before the drawer can be closed again (as the top-up drawer). */
const PAY_STALL_MS = 60_000;

const moneyOf = (amount: number | null | undefined, currency: string | null | undefined): string | null =>
  amount == null || !currency ? null : formatWithCurrencyLabel(new Intl.NumberFormat(undefined, { style: "currency", currency }), amount, currency);

/** What this event pays — on a step up, the difference. */
const priceOf = (option: EventUpgradeOption): string | null => moneyOf(option.amount, option.currency);

export const EventPlansDrawer: React.FC<EventPlansDrawerProps> = ({ opened, onClose, eventId, doing, offerUpgrade, onPublish, onSubscribe, onOpenFeatures, source }) => {
  const { t } = useTranslation(["events", "common"]);
  const crew = useCrewAccess();
  const upgrade = useEventUpgrade(eventId, opened);
  const { check, options, loading, lastError, buyError, phase, chosen, clientSecret, publishableKey } = upgrade;
  const [cardTyped, setCardTyped] = useState(false);
  const [paySubmitting, setPaySubmitting] = useState(false);
  // Stripe has not answered for a minute (a stalled network): hand the drawer back rather than hold it for good (#126).
  const [payStalled, setPayStalled] = useState(false);
  // What Stripe said about the card — shown above the form, at the step that sent it.
  const [payError, setPayError] = useState<string | null>(null);

  const needs = useMemo(() => (check?.uses ?? []).filter((u) => !u.included), [check]);
  const ready = !!check && !check.needsUpgrade;
  // The plan this event holds, and what it can still be bought up to — only plans ABOVE it (the server decides: limitReached).
  const held = options.find((o) => o.owned) ?? null;
  const offers = options.filter((o) => !o.limitReached);
  // What the drawer shows: with a plan held, EVERY plan in the ladder's order (its list price); without one, what is for
  // sale, the one that covers what the event uses first (`orderUpgradeOptions`).
  const shown = useMemo(
    () => (held ? [...options].sort((a, b) => (a.listAmount ?? Number.POSITIVE_INFINITY) - (b.listAmount ?? Number.POSITIVE_INFINITY)) : offers),
    [held, options, offers],
  );
  // ROADMAP #60 — the in-session offer on these prices while it is open (ms_auth quotes it, and charges by the same rule).
  const offered = offers.find((o) => (o.offerPercentOff ?? 0) > 0 && (o.offerSecondsLeft ?? 0) > 0) ?? null;
  const offerLeft = useOfferCountdown(offered?.offerSecondsLeft);
  const { load } = upgrade;
  const stripePromise = useMemo(() => (publishableKey ? getPlatformStripe(publishableKey) : null), [publishableKey]);
  // What an upgrade ADDS to what the event already has: the account's own plan, and on a step up the plan it holds.
  const accountFeatures = crew.planFeatures;
  const heldFeatures = useMemo(() => planFeatureMapOf(held?.planFeatures ?? []), [held]);

  // #60 — the plans were SEEN: once per opening, when the prices have been read (an offer, a step up: the price shown).
  const viewSent = useRef(false);

  useEffect(() => {
    if (!opened) {
      viewSent.current = false;

      return;
    }

    if (viewSent.current || loading || !offerUpgrade || offers.length === 0) {
      return;
    }

    viewSent.current = true;
    trackUpgradeView(
      offers.map((o) => ({ sku: o.providerSku, name: o.planName || o.name, amount: o.amount, currency: o.currency })),
      source,
    );
  }, [opened, loading, offerUpgrade, offers, source]);

  // The grant arrived: this screen's gates read the event's access — read it again; opened from Publish, and nothing is
  // missing any more, publish (owner: "Pay, then publish").
  useEffect(() => {
    if (!opened || phase !== "arrived") {
      return;
    }

    void crew.reload();

    if (ready && onPublish) {
      onPublish();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, phase, ready]);

  useEffect(() => {
    setCardTyped(false);
    setPaySubmitting(false);
    setPayError(null);
  }, [clientSecret, phase]);

  // #60 — the offer ran out: read the prices again rather than keep showing a discount the payment would no longer give.
  // ⓘ Only between purchases: a payment already started keeps the price its intent was made at.
  useEffect(() => {
    if (opened && offered && offerLeft === 0 && phase === "idle") {
      void load();
    }
  }, [opened, offered, offerLeft, phase, load]);

  useEffect(() => {
    setPayStalled(false);

    if (!paySubmitting) {
      return undefined;
    }

    const timer = setTimeout(() => setPayStalled(true), PAY_STALL_MS);

    return () => clearTimeout(timer);
  }, [paySubmitting]);

  /** What a plan gives beyond the account's own plan — and, for one bought over the event's plan, beyond that too. */
  const adds = (option: EventUpgradeOption, overHeld: boolean): string[] => {
    const plan = planFeatureMapOf(option.planFeatures ?? []);

    return Object.values(plan)
      .filter((f) => isBooleanFeature(f.featureCode) && String(f.featureValue).toLowerCase() === "true")
      .filter((f) => String(accountFeatures?.[f.featureCode]?.featureValue ?? "").toLowerCase() !== "true")
      .filter((f) => !overHeld || String(heldFeatures[f.featureCode]?.featureValue ?? "").toLowerCase() !== "true")
      .map((f) => featureDisplayName(f.featureCode, f));
  };

  const optionCard = (option: EventUpgradeOption) => {
    // The event's own plan; one below it (included in it — the server's `limitReached`); else one it can be bought up to.
    const isHeld = !!option.owned;
    const isBelow = !!held && !isHeld && !!option.limitReached;
    const forSale = !isHeld && !isBelow;
    const coverage = upgradeCoverage(option, check?.uses ?? []);
    const price = forSale ? priceOf(option) : null;
    // What it gives: for sale, what it ADDS to the event's plan; the event's own (or one below), what it includes.
    const listed = adds(option, forSale);
    const highlighted = isHeld || (forSale && coverage.covers && needs.length > 0);

    return (
      <Card key={option.providerSku} withBorder radius="md" padding="lg" style={highlighted ? { borderColor: "var(--mantine-color-brand-5)", borderWidth: 2 } : undefined}>
        <Stack gap="sm" h="100%">
          <Group justify="space-between" align="center" wrap="nowrap">
            <Group gap="xs" wrap="wrap" style={{ minWidth: 0 }}>
              <Text fw={700} size="lg">
                {option.planName || option.name}
              </Text>
              {isHeld ? (
                <Badge variant="light" color="jade" size="md" style={{ flexShrink: 0 }}>
                  {t("events:plans.currentBadge", { defaultValue: "This event's plan" })}
                </Badge>
              ) : null}
            </Group>
            {price ? (
              <Group gap={8} wrap="nowrap" align="baseline">
                {/* #60 — under the in-session offer: what it costs without it, struck through. */}
                {option.offerPercentOff && option.amountBeforeOffer != null ? (
                  <Text size="md" c="dimmed" td="line-through">
                    {moneyOf(option.amountBeforeOffer, option.currency)}
                  </Text>
                ) : null}
                <Text fw={700} size="lg">
                  {price}
                </Text>
              </Group>
            ) : null}
          </Group>

          {/* A step up says what the price is the difference of, so it is not read as the plan's own price. */}
          {option.upgradeFromPlanName && price ? (
            <Text size="md" c="dimmed">
              {t("events:plans.stepUpPrice", {
                defaultValue: "{{plan}} is {{full}} — you pay only the difference from {{from}}.",
                plan: option.planName || option.name,
                full: moneyOf(option.listAmount, option.currency) ?? "",
                from: option.upgradeFromPlanName,
              })}
            </Text>
          ) : null}

          {forSale && needs.length > 0 ? (
            coverage.covers ? (
              <Group gap={6} wrap="nowrap" align="flex-start">
                <ThemeIcon color="jade" variant="light" size={20} radius="xl" mt={2}>
                  <AppIcon iconName="Check" size={13} />
                </ThemeIcon>
                <Text size="md">{t("events:plans.covers", { defaultValue: "Covers everything this event uses." })}</Text>
              </Group>
            ) : (
              <Text size="md" c="dimmed">
                {t("events:plans.doesNotCover", { defaultValue: "Does not include: {{list}}.", list: coverage.missing.join(", ") })}
              </Text>
            )
          ) : null}

          {listed.length > 0 ? (
            <List spacing={4} size="md" center icon={<AppIcon iconName={forSale ? "Plus" : "Check"} size={14} />}>
              {listed.map((name) => (
                <List.Item key={name}>{name}</List.Item>
              ))}
            </List>
          ) : null}

          {forSale ? (
            <Button
              mt="auto"
              variant={coverage.covers ? "filled" : "light"}
              leftSection={<AppIcon iconName="CreditCard" size={16} />}
              disabled={!option.purchasable || phase !== "idle"}
              loading={phase === "creating" && chosen?.providerSku === option.providerSku}
              onClick={() => {
                trackUpgradeCheckout({ sku: option.providerSku, name: option.planName || option.name, amount: option.amount, currency: option.currency }, source);
                void upgrade.buy(option);
              }}
            >
              {price
                ? t("events:plans.upgradeFor", { defaultValue: "Upgrade for {{price}}", price })
                : t("events:plans.notForSaleYet", { defaultValue: "Not on sale yet" })}
            </Button>
          ) : (
            // Not a choice any more: said on a button-shaped line so the cards still line up, and never pressable.
            <Button mt="auto" variant="light" disabled leftSection={<AppIcon iconName="Check" size={16} />}>
              {isHeld
                ? t("events:plans.currentPlan", { defaultValue: "Current plan" })
                : t("events:plans.includedIn", { defaultValue: "Included in {{plan}}", plan: held?.planName || held?.name || "" })}
            </Button>
          )}

          {/* A refused purchase is said beside the button that asked (owner's rule), not at the top of the drawer. */}
          {forSale && buyError && chosen?.providerSku === option.providerSku ? (
            <Alert variant="light" color="red" radius="md" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
              <Text size="md">{refusalReason(buyError) || t("events:plans.buyFailed", { defaultValue: "The upgrade could not be started. Try again in a moment." })}</Text>
            </Alert>
          ) : null}
        </Stack>
      </Card>
    );
  };

  const body = () => {
    if (loading && !check) {
      return (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      );
    }

    if (phase === "paying" && clientSecret && stripePromise) {
      return (
        <Stack gap="md">
          <Text size="md">
            {t("events:plans.paying", { defaultValue: "{{plan}} for this event — paid once, nothing renews.", plan: chosen?.planName || chosen?.name || "" })}
          </Text>
          {/* ⚠ Says what closing now means: the charge may still land, and the upgrade with it — never "nothing was charged". */}
          {payStalled ? (
            <Alert variant="light" color="yellow" radius="md" icon={<AppIcon iconName="Clock" size={18} />}>
              <Text size="md">
                {t("events:plans.payStalled", {
                  defaultValue: "The payment is taking longer than usual. You can close this — if the payment goes through, the event is upgraded on its own.",
                })}
              </Text>
            </Alert>
          ) : null}
          {payError ? (
            <Alert variant="light" color="red" radius="md" icon={<AppIcon iconName="AlertCircle" size={18} />}>
              <Text size="md">{payError}</Text>
            </Alert>
          ) : null}
          <Elements stripe={stripePromise} options={{ clientSecret }}>
            <PlatformPayStep
              amountLabel={chosen ? priceOf(chosen) : null}
              onPaid={upgrade.paid}
              onError={setPayError}
              onBack={upgrade.back}
              onCardTyped={setCardTyped}
              onSubmittingChange={setPaySubmitting}
            />
          </Elements>
        </Stack>
      );
    }

    if (phase === "confirming" || phase === "creating") {
      return (
        <Group gap="sm" py="lg">
          <Loader size="sm" />
          <Text size="md">{t("events:plans.confirming", { defaultValue: "Confirming your upgrade…" })}</Text>
        </Group>
      );
    }

    if (phase === "slow") {
      return (
        <Alert variant="light" color="yellow" radius="md" icon={<AppIcon iconName="Clock" size={18} />}>
          <Stack gap="sm">
            <Text size="md" fw={600}>
              {t("events:plans.slowTitle", { defaultValue: "Your payment went through" })}
            </Text>
            <Text size="md">
              {t("events:plans.slowBody", { defaultValue: "The upgrade is on its way and usually lands within a minute. Nothing more to pay." })}
            </Text>
            <Group>
              <Button variant="light" onClick={upgrade.checkAgain}>
                {t("events:plans.checkAgain", { defaultValue: "Check again" })}
              </Button>
            </Group>
          </Stack>
        </Alert>
      );
    }

    return (
      <Stack gap="md">
        {lastError ? (
          <Alert variant="light" color="red" radius="md" icon={<AppIcon iconName="AlertTriangle" size={18} />}>
            <Text size="md">{refusalReason(lastError) || t("events:plans.loadFailed", { defaultValue: "This could not be loaded. Try again in a moment." })}</Text>
          </Alert>
        ) : null}

        {phase === "arrived" ? (
          <Alert variant="light" color="teal" radius="md" icon={<AppIcon iconName="Check" size={18} />}>
            <Text size="md" fw={600}>
              {t("events:plans.arrived", { defaultValue: "This event is now on {{plan}}.", plan: chosen?.planName || chosen?.name || "" })}
            </Text>
          </Alert>
        ) : null}

        {needs.length > 0 ? (
          <Alert
            variant="light"
            color="yellow"
            radius="md"
            icon={<AppIcon iconName="Crown" size={18} />}
            title={
              doing === "go_live"
                ? t("events:plans.needsTitleGoLive", { defaultValue: "To switch to live payments, this event needs a plan that includes:" })
                : t("events:plans.needsTitle", { defaultValue: "To publish, this event needs a plan that includes:" })
            }
          >
            <Stack gap="sm">
              <List spacing={4} size="md">
                {needs.map((u) => (
                  <List.Item key={u.featureCode}>{planUseLabel(u)}</List.Item>
                ))}
              </List>
              <Text size="md">
                {t("events:plans.orTakeOff", { defaultValue: "Or take them off the event first — switch the features off, and remove what uses them." })}
              </Text>
              <Group>
                <Button variant="default" leftSection={<AppIcon iconName="LayoutGrid" size={16} />} onClick={onOpenFeatures}>
                  {t("events:plans.openFeatures", { defaultValue: "Event features" })}
                </Button>
              </Group>
            </Stack>
          </Alert>
        ) : ready && onPublish ? (
          <Alert variant="light" color="teal" radius="md" icon={<AppIcon iconName="Check" size={18} />}>
            <Text size="md">{t("events:plans.ready", { defaultValue: "Everything this event uses is included — it is ready to publish." })}</Text>
          </Alert>
        ) : null}

        {offerUpgrade ? (
          <>
            {held ? (
              <Group gap="sm" wrap="nowrap" align="flex-start">
                {/* Never squeezed by the sentence beside it (a phone showed "V."). */}
                <Badge variant="light" color="brand" size="lg" style={{ flexShrink: 0 }}>
                  {held.planName || held.name}
                </Badge>
                <Text size="md">
                  {offers.length > 0
                    ? t("events:plans.heldCanStep", { defaultValue: "This event is on {{plan}}. Upgrade it further and pay only the difference.", plan: held.planName || held.name })
                    : t("events:plans.heldTop", { defaultValue: "This event is on {{plan}}, the highest upgrade.", plan: held.planName || held.name })}
                </Text>
              </Group>
            ) : (
              <Text size="md" c="dimmed">
                {t("events:plans.description", { defaultValue: "An upgrade applies to this event only, and is paid once." })}
              </Text>
            )}
            {/* Every plan once the event holds one (its own marked); only the plans above it can be bought. */}
            {/* #60 — the in-session offer, while it lasts (the server's seconds, counted down here). */}
            {offered && offerLeft > 0 ? (
              <Group gap="xs" wrap="nowrap" align="flex-start">
                <AppIcon iconName="Clock" size={18} style={{ marginTop: 3, flexShrink: 0 }} />
                <Text size="md" fw={600}>
                  {t("events:plans.offerLine", {
                    defaultValue: "{{percent}}% off for the next {{time}} — this event's first-hour offer.",
                    percent: offered.offerPercentOff,
                    time: formatOfferCountdown(offerLeft),
                  })}
                </Text>
              </Group>
            ) : null}
            {shown.length > 0 ? (
              <SimpleGrid cols={{ base: 1, sm: Math.min(shown.length, 2) }} spacing="md">
                {shown.map(optionCard)}
              </SimpleGrid>
            ) : !held && !loading && !lastError ? (
              <Text size="md" c="dimmed">
                {t("events:plans.none", { defaultValue: "No upgrades are on sale yet." })}
              </Text>
            ) : null}
          </>
        ) : needs.length > 0 ? (
          <Group>
            <Button leftSection={<AppIcon iconName="Rocket" size={16} />} onClick={onSubscribe}>
              {t("events:plans.seeSubscription", { defaultValue: "See plans" })}
            </Button>
          </Group>
        ) : null}
      </Stack>
    );
  };

  // Publish is offered here only when nothing is missing and nothing is in flight.
  const canPublishHere = !!onPublish && ready && (phase === "idle" || phase === "arrived");

  return (
    <AppDrawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        offerUpgrade
          ? t("events:plans.title", { defaultValue: "Upgrade this event" })
          : doing === "go_live"
            ? t("events:plans.needsDrawerTitleGoLive", { defaultValue: "What going live needs" })
            : t("events:plans.needsDrawerTitle", { defaultValue: "What publishing needs" })
      }
      actionType={canPublishHere ? "confirm-cancel" : "close"}
      onConfirm={onPublish}
      confirmLabel={t("events:plans.publish", { defaultValue: "Publish" })}
      confirmIcon="Rocket"
      // The card form is the only work here; while a payment confirms, nothing closes the drawer under it.
      isDirty={phase === "paying" && cardTyped}
      closable={(!paySubmitting || payStalled) && phase !== "creating"}
    >
      {body()}
    </AppDrawer>
  );
};
